// @docs:
//   - docs/core/architecture/SHIPS_PAPERS.md
// Bulk import — the publish engine. Pure of React: the wizard injects the API
// calls, the pause/stop switches and a ledger, and mirrors the ledger into its
// state through `onChange`. The ledger — not React state — is the truth about
// each file, so a retry sees what the last run really did:
//
//   hash → (lookup by sha) → create document → upload → attach to the library
//
// - A document whose upload fails is deleted again, so a failed run leaves no
//   empty documents behind. If that delete fails too, the ledger keeps its id
//   and the retry uploads into it rather than creating another.
// - A full vessel (413 quota) or a full library (LIBRARY_ITEM_CAP) stops the
//   batch with a message; files not yet started are "skipped" and retried.
// - Being on the vessel and being in the library are tracked apart: a failed
//   attach is retried without uploading anything again.
// - Nothing is ever left "hashing" or "uploading" once a run returns.

import { isAxiosError } from 'axios';
import type { Proposal } from './bulkImportRules';

/** vortex-api services/docLibraryService.ts LIBRARY_ITEM_CAP. */
export const LIBRARY_ITEM_CAP = 1000;

export type ItemStatus = 'queued' | 'hashing' | 'uploading' | 'attaching' | 'published' | 'attached' | 'failed' | 'skipped';

export interface ItemRecord {
  status: ItemStatus;
  detail?: string;
  sha?: string;
  /** The vessel document holding this file — created by us, or found by its hash. */
  documentId?: string;
  /** The file's bytes are on that document. */
  hasFile?: boolean;
  /** Nothing was uploaded: the bytes were already on the vessel (or earlier in this run). */
  reused?: boolean;
  /** Attached to the target library — the item is done. */
  inLibrary?: boolean;
}
export type Ledger = Map<string, ItemRecord>;

export interface EngineDeps {
  hash(p: Proposal): Promise<string>;
  lookup(sha256s: string[]): Promise<Array<{ sha256: string; documentId: string }>>;
  create(p: Proposal): Promise<{ id: string }>;
  upload(documentId: string, p: Proposal): Promise<void>;
  remove(documentId: string): Promise<void>;
  attach(documentIds: string[]): Promise<{ added: number; skipped: number }>;
  isPaused(): boolean;
  isStopped(): boolean;
  sleep?(ms: number): Promise<void>;
}

export interface RunOptions {
  /** Library slots left: LIBRARY_ITEM_CAP minus what the library already holds. */
  room: number;
  /** Files hashed and looked up together, then attached in one call. */
  wave?: number;
  /** Uploads in flight. */
  concurrency?: number;
}

export type StopReason = 'quota' | 'library_full' | 'cancelled';
export interface RunResult { stopped: { reason: StopReason; message: string } | null }

const TRANSIENT: ItemStatus[] = ['queued', 'hashing', 'uploading', 'attaching'];

const statusOf = (e: unknown) => (isAxiosError(e) ? e.response?.status : undefined);
const messageOf = (e: unknown, fallback: string) => {
  const data = isAxiosError(e) ? (e.response?.data as { error?: string } | undefined) : undefined;
  return data?.error || fallback;
};
/** 413 is the quota — unless it is the per-file size cap, which fails that file alone. */
const isQuota = (e: unknown) => statusOf(e) === 413 && !/files can be up to/i.test(messageOf(e, ''));
const isLibraryFull = (e: unknown) => statusOf(e) === 400 && /holds up to/i.test(messageOf(e, ''));

export const LIBRARY_FULL_MESSAGE = `This library is full (${LIBRARY_ITEM_CAP} documents). Nothing more was added — remove some documents, or publish the rest into another library.`;

export async function runPublish(
  items: Proposal[], ledger: Ledger, deps: EngineDeps, opts: RunOptions,
  onChange: (key: string, rec: ItemRecord) => void,
): Promise<RunResult> {
  const wave = opts.wave ?? 20;
  const concurrency = opts.concurrency ?? 3;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const set = (key: string, patch: Partial<ItemRecord>) => {
    const rec = { ...(ledger.get(key) ?? { status: 'queued' as const }), ...patch };
    ledger.set(key, rec);
    onChange(key, rec);
    return rec;
  };
  const rec = (key: string) => ledger.get(key) as ItemRecord;
  const waitWhilePaused = async () => { while (deps.isPaused() && !deps.isStopped()) await sleep(400); };

  let stopped: RunResult['stopped'] = null;
  const stop = (reason: StopReason, message: string) => { if (!stopped) stopped = { reason, message }; };
  let room = opts.room;

  // Files already on a document — from earlier runs, or found by hash in this one.
  const onVessel = new Map<string, { documentId: string; reused: boolean }>();
  for (const r of ledger.values()) if (r.sha && r.documentId && r.hasFile) onVessel.set(r.sha, { documentId: r.documentId, reused: true });

  const todo = items.filter((p) => !ledger.get(p.key)?.inLibrary);
  for (const p of todo) set(p.key, { status: 'queued', detail: undefined });

  try {
    for (let w = 0; w < todo.length && !stopped; w += wave) {
      await waitWhilePaused();
      if (deps.isStopped()) { stop('cancelled', 'Stopped. Retry picks up where it left off.'); break; }
      const batch = todo.slice(w, w + wave);

      // 1) hash what has no hash yet
      for (const p of batch) {
        if (rec(p.key).sha) continue;
        await waitWhilePaused();
        set(p.key, { status: 'hashing' });
        try { set(p.key, { sha: await deps.hash(p), status: 'queued' }); }
        catch { set(p.key, { status: 'failed', detail: 'could not read the file' }); }
      }

      // 2) which of these are already on the vessel?
      const needLookup = batch.filter((p) => rec(p.key).status !== 'failed' && !rec(p.key).hasFile && !onVessel.has(rec(p.key).sha as string));
      const shas = [...new Set(needLookup.map((p) => rec(p.key).sha as string))];
      if (shas.length) {
        try {
          for (const r of await deps.lookup(shas)) if (!onVessel.has(r.sha256)) onVessel.set(r.sha256, { documentId: r.documentId, reused: true });
        } catch (e) {
          const why = `could not check what is already on the vessel — ${messageOf(e, 'network error')}`;
          for (const p of needLookup) set(p.key, { status: 'failed', detail: why });
        }
      }

      // 3) upload what is missing, `concurrency` at a time
      const ready: Array<{ key: string; documentId: string }> = [];
      let next = 0;
      const worker = async () => {
        for (;;) {
          const p = batch[next++];
          if (!p) return;
          if (rec(p.key).status === 'failed') continue;
          await waitWhilePaused();
          if (deps.isStopped()) stop('cancelled', 'Stopped. Retry picks up where it left off.');
          if (!stopped && room <= 0) stop('library_full', LIBRARY_FULL_MESSAGE);
          if (stopped) { set(p.key, { status: 'skipped', detail: 'not started' }); continue; }
          room--;   // reserved: this file will take a library slot

          const r = rec(p.key);
          if (r.hasFile && r.documentId) { ready.push({ key: p.key, documentId: r.documentId }); continue; }
          const found = onVessel.get(r.sha as string);
          if (found && found.documentId !== r.documentId) {
            // Same bytes are already on the vessel. A leftover empty document from an earlier failure goes.
            if (r.documentId) await deps.remove(r.documentId).catch(() => undefined);
            set(p.key, { documentId: found.documentId, hasFile: true, reused: true, detail: 'already on the vessel — attached' });
            ready.push({ key: p.key, documentId: found.documentId });
            continue;
          }

          set(p.key, { status: 'uploading' });
          let documentId = r.documentId;
          try {
            if (!documentId) documentId = (await deps.create(p)).id;
            set(p.key, { documentId });
          } catch (e) {
            room++;
            set(p.key, { status: 'failed', detail: `could not create the document — ${messageOf(e, 'network error')}` });
            continue;
          }
          try {
            await deps.upload(documentId, p);
            set(p.key, { hasFile: true, reused: false });
            onVessel.set(r.sha as string, { documentId, reused: false });
            ready.push({ key: p.key, documentId });
          } catch (e) {
            room++;
            const msg = messageOf(e, 'upload failed');
            if (isQuota(e)) stop('quota', `${msg}. Nothing more was uploaded — free some space or move to a larger plan, then retry.`);
            // No empty documents: take back the one just created.
            let keep: string | undefined = documentId;
            try { await deps.remove(documentId); keep = undefined; } catch { /* the retry uploads into it instead */ }
            set(p.key, { status: 'failed', detail: msg, documentId: keep, hasFile: false });
          }
        }
      };
      await Promise.all(Array.from({ length: concurrency }, worker));

      // 4) attach the wave to the library
      if (ready.length) {
        for (const x of ready) set(x.key, { status: 'attaching' });
        try {
          const r = await deps.attach([...new Set(ready.map((x) => x.documentId))]);
          room += ready.length - r.added;   // files already in the library took no new slot
          for (const x of ready) set(x.key, { status: rec(x.key).reused ? 'attached' : 'published', inLibrary: true });
        } catch (e) {
          room += ready.length;
          if (isLibraryFull(e)) stop('library_full', LIBRARY_FULL_MESSAGE);
          const why = `on the vessel, not yet in the library (${messageOf(e, 'network error')}) — retry adds it`;
          for (const x of ready) set(x.key, { status: 'failed', detail: why, inLibrary: false });
        }
      }
    }
  } finally {
    for (const p of todo) {
      const r = ledger.get(p.key);
      if (!r || !TRANSIENT.includes(r.status)) continue;
      if (r.status === 'queued') set(p.key, stopped ? { status: 'skipped', detail: 'not started' } : { status: 'failed', detail: 'interrupted' });
      else set(p.key, { status: 'failed', detail: 'interrupted' });
    }
  }
  return { stopped };
}

export interface Summary { published: number; attached: number; failed: number; skipped: number; pending: number; doneBytes: number; total: number }

/** Counts for the progress line. `pending` = not yet in the library and not failed/skipped. */
export function summarize(items: Proposal[], ledger: Ledger): Summary {
  const s: Summary = { published: 0, attached: 0, failed: 0, skipped: 0, pending: 0, doneBytes: 0, total: items.length };
  for (const p of items) {
    const st = ledger.get(p.key)?.status;
    if (st === 'published') { s.published++; s.doneBytes += p.sizeBytes; }
    else if (st === 'attached') s.attached++;
    else if (st === 'failed') s.failed++;
    else if (st === 'skipped') s.skipped++;
    else s.pending++;
  }
  return s;
}
